import React, { useReducer } from "react";
import PropTypes from "prop-types";
import { v4 as uuid } from "uuid";
import { CardPropType } from "./misc/custom-prop-types";
import { cardFormReducer } from "./misc/card-form-reducer";
import * as GlobalStyled from "../styled/form-fields";
import ImageModal from "./ImageModal";
import styled from "styled-components";
import { mediaUrl } from "../../../helpers/CoreHelpers";
import BtnPrimary from "../UI/BtnPrimary";

const HField = styled.div`
  display: flex;

  .image-field {
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 1;

    &__img {
      border-radius: 50%;
      background-size: cover;
      background-position: center;
      border: 6px solid #c6c5c5;
      margin-right: 1rem;
      opacity: 1;
      transition: opacity 0.25s ease-in-out;
      width: 200px;
      height: 200px;

      ${(props) =>
        !props.isReadOnly &&
        `
      &:hover {
        cursor: pointer;
        opacity: 0.8;
      }
      `}
    }
  }

  .about-field {
    flex: 2;
  }
`;

export const FormType = Object.freeze({
  ReadOnly: "ReadOnly",
  Create: "Create",
  Edit: "Edit",
});

const CardForm = (props) => {
  const { formData, formType, onAdd, onCancel } = props;

  const [state, dispatch] = useReducer(cardFormReducer, {
    values: { ...formData },
    errors: {},
    showMediaModal: false,
  });

  const handleOnChange = ({ target }) => {
    if (formType === FormType.ReadOnly) {
      return;
    }

    const { name, type } = target;
    const value = type === "checkbox" ? target.checked : target.value;

    dispatch({
      type: "UPDATE_FORM_FIELD",
      payload: { name, value },
    });
  };

  const handleOnChangeImage = (img) => {
    dispatch({
      type: "UPDATE_FORM_FIELD",
      payload: {
        name: "imgSrc",
        value: img,
      },
    });
    dispatch({
      type: "SHOW_MEDIA_MODAL",
      payload: false,
    });
  };

  const handleOnSubmit = (e) => {
    e.preventDefault();

    onAdd({
      ...state.values,
      order: !isNaN(state.values.order) ? parseInt(state.values.order, 10) : 0,
    });
  };

  const onOpenModalHandler = (e) => {
    e.preventDefault();

    if (formType === FormType.ReadOnly) {
      return;
    }

    dispatch({
      type: "SHOW_MEDIA_MODAL",
      payload: true,
    });
  };

  const onCloseModalHandler = () => {
    dispatch({
      type: "SHOW_MEDIA_MODAL",
      payload: false,
    });
  };

  const {
    values: { title, description, btnText, link, imgSrc, order = 0 },
    showMediaModal,
  } = state;

  const isReadOnly = formType === FormType.ReadOnly;

  return (
    <div
      style={{
        borderLeft: "4px solid #ffc12d",
        paddingLeft: "20px",
        margin: "20px 0px",
      }}
    >
      <HField isReadOnly={isReadOnly}>
        <div className="image-field">
          {imgSrc ? (
            <div
              onClick={onOpenModalHandler}
              style={{ backgroundImage: `url(${mediaUrl(imgSrc)})` }}
              className="image-field__img"
            />
          ) : (
            <BtnPrimary
              disabled={isReadOnly}
              small
              onClick={onOpenModalHandler}
            >
              Select image
            </BtnPrimary>
          )}
        </div>
        <div className="about-field">
          <GlobalStyled.FormField>
            <GlobalStyled.Label>Title</GlobalStyled.Label>
            <GlobalStyled.Input
              readOnly={isReadOnly}
              type="text"
              name="title"
              value={title}
              onChange={handleOnChange}
              disabled={isReadOnly}
            />
          </GlobalStyled.FormField>
          <div
            style={{ marginBottom: "16px" }}
            className="csod-awave-- csod-awave--field csod-awave--field--text"
          >
            <label className="csod-awave--field__label">Description</label>
            <textarea
              style={{ background: isReadOnly ? "#e9ecef" : "#fff" }}
              readOnly={isReadOnly}
              name="description"
              value={description}
              onChange={handleOnChange}
              disabled={isReadOnly}
            />
          </div>
        </div>
      </HField>
      <GlobalStyled.FormField>
        <GlobalStyled.Label>Button text</GlobalStyled.Label>
        <GlobalStyled.Input
          readOnly={isReadOnly}
          type="text"
          name="btnText"
          value={btnText}
          onChange={handleOnChange}
          disabled={isReadOnly}
        />
      </GlobalStyled.FormField>
      <GlobalStyled.FormField>
        <GlobalStyled.Label>Link</GlobalStyled.Label>
        <GlobalStyled.Input
          readOnly={isReadOnly}
          type="text"
          name="link"
          value={link}
          onChange={handleOnChange}
          disabled={isReadOnly}
        />
      </GlobalStyled.FormField>
      <GlobalStyled.FormField>
        <GlobalStyled.Label>Sort order</GlobalStyled.Label>
        <GlobalStyled.Input
          placeholder="invalid value defaults to 0"
          readOnly={isReadOnly}
          type="text"
          name="order"
          value={order}
          onChange={handleOnChange}
          disabled={isReadOnly}
        />
      </GlobalStyled.FormField>
      <div>
        {formType !== FormType.ReadOnly && (
          <a
            style={{ marginRight: "16px" }}
            role="button"
            onClick={handleOnSubmit}
          >
            {formType === FormType.Create ? "Add" : "Save"}
          </a>
        )}
        <a role="button" onClick={onCancel}>
          Cancel
        </a>
      </div>
      <ImageModal
        open={showMediaModal}
        onClose={onCloseModalHandler}
        onChange={handleOnChangeImage}
      />
    </div>
  );
};

CardForm.propTypes = {
  formData: CardPropType,
  formType: PropTypes.oneOf(Object.values(FormType)).isRequired,
  onAdd: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

CardForm.defaultProps = {
  formData: {
    id: uuid(),
    imgSrc: null,
    title: "",
    description: "",
    btnText: "",
    link: "",
  },
};

export default CardForm;



// WEBPACK FOOTER //
// ./src/core/admin/components/CardGrid/CardForm.js